// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tutorial.ts — six steps on the real board, as DATA.
//
// IT DRIVES THE SHIPPED STATE MACHINE. There is no mock board, no parallel rule set and no scripted
// animation: `setup()` builds a genuine `State` with the real `createState` and the real `spawn`,
// and every step is satisfied by the player issuing a genuine command through the same path a live
// match uses. A tutorial with a private implementation is a lie the moment anybody touches the
// rules, and worse, it is not even a DETECTABLE lie — it goes on confidently teaching the old game
// for ever. Because the steps are data, `tests/tutorial.test.ts` can replay every one of them
// through the real simulation and assert each is legal and reachable from the state the previous
// one produced; a rules change turns the tutorial red instead of silently teaching a ghost.
//
// THE LAST STEP IS THE POINT. Everything before it is scaffolding to reach the moment where the
// player salvages a wreck for thirty-five ore and the enemy artillery it had been blocking
// immediately fires through the gap. That trade — your income is your cover, and taking one
// destroys the other — is the whole game, and it is not something anybody learns from a rules card.

import { Cmd, Stance, createState, spawn, tileOf, type State } from './sim';
import { ROWS, idx, rowOf } from './map';
import { UnitType } from './units';
import type { Order } from './control';

export interface Step {
  id: string;
  /** One sentence, imperative, naming what to touch. */
  text: string;
  /** Said once the step is done — the WHY, after the player has seen the what. */
  after: string;
  /** A tile to ring on the board, or −1. */
  hint(s: State): number;
  /** Is this order allowed right now? Everything else is refused without breaking the sequence. */
  allows(s: State, o: Order): boolean;
  /** Has the player done the thing? */
  done(s: State, selection: ReadonlySet<number>): boolean;
}

/**
 * The tutorial's geometry, and every tile in it was chosen against the real map rather than guessed.
 *
 * Column 8 is the demonstration lane: the Arbalest at (8,11), the Anvil exactly between it and the
 * amber unit at (8,7), four tiles away — inside the Arbalest's four-and-a-half-tile reach, so the
 * shot is genuinely available and genuinely blocked BY THE ANVIL.
 *
 * Getting there took three attempts and the test caught every one. Column 6 is a gate ridge. Column
 * 3 puts the Anvil's own body next to the seam step 2 is about. Column 10 looks clear and is not:
 * (10,10) is a corner ridge, so the Arbalest could never see the target at all — the step "worked"
 * for entirely the wrong reason, and the test passed because it only asserted that no damage was
 * done. tests/tutorial.test.ts now asserts the POSITIVE control too: move the Anvil aside and the
 * shot must land. That is the assertion the mutation check actually exercises.
 */
export const SEAM_TILE = idx(4, 9);
export const ANVIL_TILE = idx(8, 9);
export const ARB_TILE = idx(8, 11);
export const FOE_TILE = idx(8, 7);
export const CLEAR_TILE = idx(9, 9);

/**
 * The board the tutorial plays on: the real Standing map, with six bodies placed by hand so the
 * lesson can be reached in ninety seconds instead of after two nine-second build timers.
 */
export function setup(): State {
  const s = createState(20260820, 'standing');
  // createState has already placed the two opening scouts for each side; add the pieces the lesson
  // needs. Ids stay sequential, so the tutorial's unit references are stable.
  spawn(s, 0, UnitType.Anvil, ANVIL_TILE);
  spawn(s, 0, UnitType.Arbalest, ARB_TILE);
  spawn(s, 1, UnitType.Harrier, FOE_TILE);
  // The opposition stands still for the whole lesson. A tutorial whose scripted opponent wanders
  // off is a tutorial whose steps stop being reachable.
  for (let e = 0; e < s.nextId; e++) if (s.seat[e] === 1) s.stance[e] = Stance.Hold;
  s.sides[0].reserve = 200;
  return s;
}

/** The units the tutorial refers to, found by role rather than by a hard-coded id. */
export function actors(s: State): { scout: number; anvil: number; arb: number; foe: number } {
  let scout = -1;
  let anvil = -1;
  let arb = -1;
  let foe = -1;
  let foeD = 1 << 30;
  for (let e = 0; e < s.nextId; e++) {
    if (!s.alive[e]) continue;
    if (s.seat[e] === 1) {
      // The NEAREST amber unit, not the first one created. Step 5 rings whichever enemy the player
      // is being sent at, and pointing at a body six tiles behind the front is a tutorial that
      // cannot be finished.
      const d = ROWS - rowOf(tileOf(s, e));
      if (d < foeD) {
        foeD = d;
        foe = e;
      }
      continue;
    }
    if (s.type[e] === UnitType.Anvil && anvil < 0) anvil = e;
    else if (s.type[e] === UnitType.Arbalest && arb < 0) arb = e;
    else if (s.type[e] === UnitType.Harrier && scout < 0) scout = e;
  }
  return { scout, anvil, arb, foe };
}

const seamOf = (s: State) => s.seams.find((x) => x.tile === SEAM_TILE);

export const STEPS: readonly Step[] = [
  {
    id: 'select',
    text: 'Tap one of your Harriers — the small triangles at the bottom.',
    after: 'That is your whole control scheme: tap a unit, then tap where it should be.',
    hint: (s) => {
      const a = actors(s);
      return a.scout >= 0 ? tileOf(s, a.scout) : -1;
    },
    allows: () => true,
    done: (s, sel) => {
      for (const e of sel) if (s.alive[e] && s.seat[e] === 0 && s.type[e] === UnitType.Harrier) return true;
      return false;
    },
  },
  {
    id: 'capture',
    text: 'Now tap the ringed seam in the middle of the board.',
    after: 'Two seconds of standing there and it is yours. It keeps paying after you walk away — until somebody else stands next to it.',
    hint: () => SEAM_TILE,
    // Only a move, and only to the seam. Everything else is refused rather than breaking the run.
    allows: (_s, o) => o.kind === Cmd.Move && o.arg === SEAM_TILE,
    done: (s) => (seamOf(s)?.owner ?? 2) === 0,
  },
  {
    id: 'build',
    text: 'Tap ANVIL to start building one.',
    after: 'That ore has left your reserve. There is no cancel and no refund, and there are only two thousand in the whole map.',
    hint: () => -1,
    allows: (_s, o) => o.kind === Cmd.Build && o.arg === UnitType.Anvil,
    done: (s) => s.sides[0].presses > 0,
  },
  {
    id: 'blocked',
    text: 'Your Arbalest has a target and cannot fire — your own Anvil is in front of it. Move the Anvil aside.',
    after: 'Only big bodies block a line of fire: your Anvils, and the wrecks they leave behind. Your own screen can silence your own artillery.',
    hint: () => CLEAR_TILE,
    allows: (s, o) => {
      const a = actors(s);
      return o.kind === Cmd.Move && o.unit === a.anvil;
    },
    done: (s) => {
      const a = actors(s);
      return a.anvil >= 0 && tileOf(s, a.anvil) !== ANVIL_TILE;
    },
  },
  {
    id: 'kill',
    text: 'Send a Harrier at the amber unit and finish it.',
    after: 'It left a wreck. That wreck is thirty-five ore to whoever stands on it — and a wall neither side can shoot through.',
    hint: (s) => {
      const a = actors(s);
      return a.foe >= 0 ? tileOf(s, a.foe) : -1;
    },
    allows: () => true,
    done: (s) => s.wrecks.length > 0,
  },
  {
    id: 'salvage',
    text: 'Walk a unit onto the wreck and take the ore.',
    after: 'And there it is: you just sold the only thing that was blocking their line. The money and the cover are the same object — that is the whole game.',
    hint: (s) => (s.wrecks.length > 0 ? s.wrecks[0].tile : -1),
    allows: () => true,
    done: (s) => s.sides[0].salvaged > 0,
  },
];

export const STEP_COUNT = STEPS.length;

/** Is this order permitted at `i`? Out-of-range indices allow everything, so the end is playable. */
export function allowed(i: number, s: State, o: Order): boolean {
  const step = STEPS[i];
  return step ? step.allows(s, o) : true;
}
